import {
    ApolloClient,
    createHttpLink,
    InMemoryCache,
    makeVar,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { Participants, Profile } from 'src/types';
import { relayStylePagination } from '@apollo/client/utilities';
import env from 'src/env';
import { Practitioner } from '@luminalearning/types';
import { locales, selectValidLocale } from 'src/lang/locales';

const currentLanguage = makeVar('');
const decodedToken = makeVar({});
const selectedParticipant = makeVar<Participants | null>(null);
const activePractitionerDetails = makeVar<Practitioner | null>(null);
const isMobileNavOpen = makeVar(false);

const cache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                appLocale: {
                    read: () => currentLanguage(),
                },
                token: {
                    read: () => decodedToken(),
                },
                projectsParticipants: relayStylePagination(),
                isLoggedIn: {
                    read() {
                        return !!localStorage.getItem('id_token');
                    },
                },
            },
        },
    },
});

const initLocale = () => {
    if (typeof window !== 'undefined') {
        // Check initial language from local storage or browser language, otherwise revert back to english uk
        const userProfile: Profile = JSON.parse(
            localStorage.getItem('profile') ?? '{}'
        );

        const localeFromProfileStorage = userProfile?.user_metadata?.locale;
        let initialLocale = localeFromProfileStorage || navigator.language;
        if (!initialLocale || !(initialLocale in locales)) {
            initialLocale = env.defaultLocale;
        }

        // Check if locale is supported
        initialLocale = selectValidLocale(initialLocale);
        localStorage.setItem('locale', initialLocale);

        currentLanguage(initialLocale);
    }
};

const initClient = () => {
    const httpLink = createHttpLink({
        uri: `${env.graphqlUrl}`,
    });

    const authLink = setContext((_, { headers }) => {
        // get the authentication token from local storage if it exists
        const token = localStorage.getItem('id_token');

        return {
            headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : '',

            },
        };
    });

    const client = new ApolloClient({
        link: authLink.concat(httpLink),
        cache,
        credentials: 'same-origin',
    });

    initLocale();

    return client;
};

export {
    cache,
    initClient,
    decodedToken,
    currentLanguage,
    selectedParticipant,
    activePractitionerDetails,
    isMobileNavOpen,
    initLocale,
};
